import PDFMake from 'pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { getFooter, fileNameForString } from '@/utils/pdf-formatters/GenericSettlementsFunctions.js'
import store from '@/store/index.js'
import moment from 'moment'
import { createCanvas } from 'canvas'
import crypto from 'crypto'
import { VehicleType } from '@/utils/Enumerations'
import { companyLogoClient, PdfLogoDimensions } from '@/utils/CompanyLogo'
import { theme } from '@/plugins/theme'
import { getLegnaQrCodeUrl } from '@/utils/LegnaQrCode'

PDFMake.vfs = pdfFonts.pdfMake.vfs

const DOTS_PER_INCH = 203
const PAGE_HEIGHT_INCH_FOR_SMALL_PDF = 5
const LABEL_HEIGHT = DOTS_PER_INCH * PAGE_HEIGHT_INCH_FOR_SMALL_PDF // Label length of 8 inches (203 * 8)
const LABEL_WIDTH = 576.52
const QR_CODE_CENTERING_DIVISION = 2
const QR_CODE_CENTERING_OFFSET_TRANSLATION = 4
const BASE_QR_UPPER_BOUND = 100
const BASE_QR_CODE_LENGTH = 2.3
const BASE_QR_CODE_MAGNIFICATION = 13
const LARGE_QR_CODE_LENGTH = 2.4
const LARGE_QR_CODE_MAGNIFICATION = 12
const DATA_START_LOCATION = 80
const AES_ALGORITHM = 'aes-256-cbc'
const KEY_SIZE = 32
const IV_SIZE = 16
const DRIVER_SHEET_QR_SIZE = 250

export function getAccountQrCodeData (account, companyInfo) {
  if (!account.code) throw new Error(`Account ${account.name} is missing code`)
  const payload = {
    t: 'DR',
    v: 1,
    ac: account.code,
    tc: companyInfo.code
  }
  return JSON.stringify(payload)
}

export function compareTrailerIdentifierValues (a, b) {
  const av = String(a.value)
  const bv = String(b.value)
  const cmp = av.localeCompare(bv, undefined, { numeric: true })
  return cmp !== 0 ? cmp : av.length - bv.length
}

export function getAccountDriverQrCodeData (account, companyInfo, trailerValue) {
  const payload = {
    type: 'driver',
    v: 1,
    id: account.accountId,
    param: trailerValue != null ? JSON.stringify({ tid: trailerValue }) : ''
  }
  const angelPayload = encrypt(JSON.stringify(payload), companyInfo.code)
  return JSON.stringify({ ap: angelPayload })
}

// Builds one driver-sheet page per selected trailer (or a single generic page when none are selected).
// Used by both the single-account screen and the bulk download.
export async function buildDriverSheetPages (account, companyInfo, trailers = []) {
  const targets = trailers.length > 0
    ? [...trailers].sort(compareTrailerIdentifierValues)
    : [null]
  const pages = []
  for (const ti of targets) {
    const text = getAccountDriverQrCodeData(account, companyInfo, ti?.value)
    const qrCode = await getLegnaQrCodeUrl(DRIVER_SHEET_QR_SIZE, text)
    pages.push({ trailerIdentifier: ti, qrCode })
  }
  return pages
}

function encrypt (message, key) {
  const paddedKey = key.padEnd(KEY_SIZE, '0')
  const keyBuffer = Buffer.from(paddedKey, 'utf8').subarray(0, KEY_SIZE)
  const iv = crypto.randomBytes(IV_SIZE)
  const cipher = crypto.createCipheriv(AES_ALGORITHM, keyBuffer, iv)
  let encrypted = cipher.update(message, 'utf8', 'base64')
  encrypted += cipher.final('base64')
  const ivBase64 = iv.toString('base64')
  return `${encrypted}:${ivBase64}`
}

export async function generateAccountIdSheetPDF (account, qrCode, download = true) {
  const companyInfo = store.getters['user/companyInfo']

  const content = [
    { text: `Account Identification Sheet for ${companyInfo.name}`, style: 'title' },
    { text: account.name, style: 'subtitle', margin: [0, 5] },
    { text: `Generated on ${moment().format('ddd MMM DD YYYY HH:mm:ss')}`, fontSize: 12, alignment: 'center', margin: [0, 5] },
    { image: qrCode, alignment: 'center' },
    { text: 'Scan code in Transporter or Loader to register a new user under this account', italics: true, fontSize: 12, alignment: 'center', margin: [0, 10] }
  ]

  const documentDefinition = {
    footer: getFooter(1, 1),

    content,

    defaultStyle: {
      margin: [0, 20]
    },

    styles: {
      title: {
        fontSize: 16,
        bold: true,
        alignment: 'center'
      },
      subtitle: {
        fontSize: 14,
        bold: false,
        alignment: 'center'
      }
    }
  }
  const fileName = `account-idsheet-${encodeURIComponent(account.code.trim())}.pdf`
  if (!download) {
    return new Promise((resolve) => {
      PDFMake.createPdf(documentDefinition).getBlob((blob) => {
        resolve({ fileName: fileName, fileData: blob })
      })
    })
  }
  PDFMake.createPdf(documentDefinition).download(fileName)
}

export function generateAccountDriverSheet (account, qrCode, download = true) {
  const companyInfo = store.getters['user/companyInfo']
  const qrCodeSizeInfo = calculateQrCodeSize(qrCode.length)
  const date = moment().format('ddd MMM DD YYYY HH:mm:ss')
  let dataYLocation = DATA_START_LOCATION
  let zpl = '^XA\n'
  zpl += `^LL${LABEL_HEIGHT}\n`
  zpl += '^CF0, 50\n'
  zpl += `^FO50,${dataYLocation}^FB500,3,0,C^FDAccount Identification Sheet for ${companyInfo.name}\\&^FS\n`
  dataYLocation += incrementLineSpacing(`Account Identification Sheet for ${companyInfo.name}`)
  zpl += '^CF0,40\n'
  zpl += `^FO50,${dataYLocation}^FB500,3,0,C^FD${account.name}\\&^FS\n`
  dataYLocation += incrementLineSpacing(account.name)
  zpl += `^FO50,${dataYLocation}^FB500,3,0,C^FDCode: ${account.code}\\&^FS\n`
  dataYLocation += incrementLineSpacing(`Code: ${account.code}`)
  zpl += '^CF0,30\n'
  zpl += `^FO50,${dataYLocation}^FB500,3,0,C^FDGenerated on ${date}\\&^FS\n`
  dataYLocation += incrementLineSpacingQr(`Generated on ${date}`)
  zpl += `^FO${calculateQrCodeXPosition(qrCodeSizeInfo)},${calculateQrCodeYPosition(qrCodeSizeInfo, dataYLocation)}^BQN,2,${qrCodeSizeInfo.magnification}^FDLA,${qrCode}^FS\n`
  zpl += `^FO0,${LABEL_HEIGHT - 40}^FB600,1,1,C^FDScan code in Kiosk to authenticate driver\\&^FS\n`
  zpl += '^XZ'

  const fileName = `account-driver-idsheet-${encodeURIComponent(account.code.trim())}.txt`
  const blob = new Blob([zpl], { type: 'text/plain;charset=utf-8' })
  if (!download) {
    return { fileName: fileName, fileData: blob }
  }
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${account.name}-${account.code}-${date}-driver-code.txt`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export async function prefetchDriverSheetLogo (companyInfo) {
  return {
    logoAsDataUrl: await companyLogoClient.getCompanyLogoAsDataUrl(companyInfo.companyInfoId),
    logoTopMargin: await companyLogoClient.getTopMarginToCenterImage(companyInfo.companyInfoId)
  }
}

export async function generateAccountDriverSheetPdf (account, pages, download = true, logoData = null) {
  const companyInfo = store.getters['user/companyInfo']
  const resolvedLogo = logoData ?? await prefetchDriverSheetLogo(companyInfo)
  const { logoAsDataUrl, logoTopMargin } = resolvedLogo
  const generatedOn = moment().format('ddd MMM DD YYYY HH:mm:ss')

  const header = () => ({
    margin: [40, 24, 40, 0],
    stack: [
      {
        columns: [
          logoAsDataUrl
            ? { image: logoAsDataUrl, maxWidth: PdfLogoDimensions.LOGO_MAX_WIDTH, maxHeight: PdfLogoDimensions.LOGO_MAX_HEIGHT, marginTop: logoTopMargin, width: PdfLogoDimensions.LOGO_MAX_WIDTH }
            : { text: '', width: PdfLogoDimensions.LOGO_MAX_WIDTH },
          {
            stack: [
              { text: companyInfo.name, style: 'companyName' },
              { text: 'Driver Identification Sheet', style: 'titleAccent' }
            ],
            alignment: 'right',
            marginTop: logoAsDataUrl ? 16 : 0
          }
        ]
      },
      { margin: [0, 8, 0, 0], canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: theme.secondary }] }
    ]
  })

  const trailerBlock = ti => ({
    margin: [80, 0, 80, 0],
    table: {
      widths: ['*'],
      body: [
        [{ text: 'Registered Trailer', style: 'trailerLabel', alignment: 'center', border: [false, false, false, false] }],
        [{ text: `${ti.value} — ${VehicleType.fromInt(ti.vehicleType)}`, style: 'trailerValue', alignment: 'center', border: [false, false, false, false] }]
      ]
    }
  })

  const content = pages.flatMap((page, idx) => [
    { text: account.name, style: 'accountName', alignment: 'center', margin: [0, 0, 0, 8], pageBreak: idx === 0 ? undefined : 'before' },
    page.qrCode ? { image: page.qrCode, alignment: 'center', margin: [0, 0, 0, 10] } : null,
    { text: 'Scan this code at the Kiosk to identify yourself.', italics: true, fontSize: 12, alignment: 'center', margin: [0, 0, 0, page.trailerIdentifier ? 24 : 0] },
    page.trailerIdentifier ? trailerBlock(page.trailerIdentifier) : null
  ].filter(Boolean))

  const footer = () => ({
    margin: [40, 0, 40, 0],
    text: `Generated on ${generatedOn}`,
    fontSize: 8,
    bold: true,
    alignment: 'center'
  })

  const documentDefinition = {
    pageMargins: [40, 120, 40, 60],
    header,
    footer,
    content,
    styles: {
      companyName: { fontSize: 16, bold: true, color: theme.secondary },
      titleAccent: { fontSize: 12, color: theme.secondary, bold: true, margin: [0, 2, 0, 0] },
      accountName: { fontSize: 18, bold: true, color: theme.secondary },
      trailerLabel: { fontSize: 10, bold: true, color: theme.secondary, margin: [0, 6, 0, 0] },
      trailerValue: { fontSize: 16, bold: true, color: theme.secondary, margin: [0, 2, 0, 8] }
    }
  }

  const fileName = `account-driversheet-${fileNameForString(account.name.trim())}.pdf`
  if (!download) {
    return new Promise(resolve => {
      PDFMake.createPdf(documentDefinition).getBlob(blob => {
        resolve({ fileName, fileData: blob })
      })
    })
  }
  PDFMake.createPdf(documentDefinition).download(fileName)
}

function calculateQrCodeSize (qrCodeSize) {
  if (qrCodeSize >= 0 && qrCodeSize <= BASE_QR_UPPER_BOUND) {
    return {
      length: BASE_QR_CODE_LENGTH,
      magnification: BASE_QR_CODE_MAGNIFICATION
    }
  } else {
    return {
      length: LARGE_QR_CODE_LENGTH,
      magnification: LARGE_QR_CODE_MAGNIFICATION
    }
  }
}

function calculateQrCodeXPosition (qrCodeSize) {
  return ((LABEL_WIDTH / QR_CODE_CENTERING_DIVISION) - ((qrCodeSize.length * DOTS_PER_INCH) / QR_CODE_CENTERING_DIVISION)) - QR_CODE_CENTERING_OFFSET_TRANSLATION
}

function calculateQrCodeYPosition (qrCodeSize, dataYLocation) {
  const endOfInformationPoint = (LABEL_HEIGHT - 40) - dataYLocation
  return dataYLocation + ((endOfInformationPoint) / QR_CODE_CENTERING_DIVISION) - ((qrCodeSize.length * DOTS_PER_INCH) / QR_CODE_CENTERING_DIVISION)
}

function incrementLineSpacing (text) {
  const textWidth = createCanvas().getContext('2d').measureText(text).width
  const numberOfLines = Math.ceil(textWidth / 100)
  return numberOfLines * 50
}

function incrementLineSpacingQr (text) {
  const textWidth = createCanvas().getContext('2d').measureText(text).width
  const numberOfLines = Math.ceil(textWidth / 100)
  return numberOfLines * 5
}
